import { INITIAL_ERROR } from '@/common/constants.ts'
import {
  REQUEST_STATUS,
  ThunkRequestState,
} from '@/common/types_and_interfaces.ts'
import { ProgramSettingsResp } from '@/storage/program_settings/requests.ts'
import { FetchProgramSettings } from '@/storage/program_settings/thunks.ts'
import { RootState } from '@/storage/store.ts'
import { createSlice } from '@reduxjs/toolkit'

export type ProgramSettingsState = {
  request: ThunkRequestState
  data: ProgramSettingsResp | null
}

const InitialRequestState: ThunkRequestState = {
  status: REQUEST_STATUS.IDLE,
  error: INITIAL_ERROR,
} as const

const initialState: ProgramSettingsState = {
  request: { ...InitialRequestState },
  data: null,
} as const

const programSettingsSlice = createSlice({
  name: 'programSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(FetchProgramSettings.pending, function (state) {
        state.request.error = INITIAL_ERROR
        state.request.status = 'in_flight'
      })
      .addCase(FetchProgramSettings.fulfilled, function (state, action) {
        state.data = action.payload
        state.request.error = INITIAL_ERROR
        state.request.status = 'success'
      })
      .addCase(FetchProgramSettings.rejected, function (state, action) {
        state.request.status = 'failed'
        // API error occurred
        if (action.payload) {
          state.request.error = action.payload
        }
        // Library error occurred
        else {
          state.request.error.message = action.error.message ?? ''
        }
      })
  },
})

export default programSettingsSlice.reducer

/**
 * Selects and returns the program ID from the state object.
 *
 * If the program ID is undefined, an error is thrown indicating that the program ID was not found.
 *
 * @param {RootState} state - The root state object containing the ProgramSettings data.
 * @throws {Error} If the program ID is not found in the state.
 * @returns The program ID from the state's ProgramSettings.
 */
export const selectProgramID = (state: RootState): number => {
  if (state.ProgramSettings.data?.id == undefined) {
    throw new Error('Program ID not found')
  }
  return state.ProgramSettings.data.id
}

export const selectProgramSettings = (state: RootState) => {
  if (!state.ProgramSettings.data) {
    throw new Error('Program Settings not found')
  }

  return state.ProgramSettings.data
}
